import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import saveAutoRejectFeedback from '../../services/saveAutoRejectFeedback';
import './AutoRejectFeedback.scss';

var moment = require('moment');

const MAX_FEEDBACK_LENGTH = 1000;
const platform = process.env.REACT_APP_PLATFORM;

// Only used for the "report a missed criterion" picker on clean jobs - flagged chips use the labels the backend sends
const missableCriteria = [
    { code: 'low_salary', label: 'Low Salary' },
    { code: 'low_salary_vs_experience', label: 'Low Salary v-a-v experience' },
    { code: 'salary_offered', label: 'Salary Offered' },
    { code: 'no_content', label: 'No content' },
    { code: 'not_relevant', label: 'Not Relevant' },
    { code: 'internship_part_time', label: 'Internship/Part Time Jobs' },
    { code: 'other_portal', label: platform == 'hirist' ? 'iimjobs.com' : 'hirist.com' },
    { code: 'official_id_issue', label: 'Official ID issue' },
    { code: 'duplicate_posting', label: 'Duplicate Posting' },
    { code: 'multiple_jds', label: 'Multiple JDs' },
    { code: 'jd_title_mismatch', label: 'JD & Title Mismatch' },
    { code: 'technical_skillset_missing', label: 'Technical Skill Set Missing', hiristOnly: true },
    { code: 'more_details', label: 'More Details' },
].filter(each => !each.hiristOnly || platform == 'hirist');

// Pairs the reason codes with their labels (same order); every field is optional because production doesn't send them yet
export const getAutoRejectReasons = (item) => {
    const codes = Array.isArray(item.autoRejectReasons) ? item.autoRejectReasons : [];
    const labels = Array.isArray(item.autoRejectReasonLabels) ? item.autoRejectReasonLabels : [];
    return codes
        .map((code, index) => ({ code, label: labels[index] || code }))
        .filter(each => typeof each.code === 'string' && each.code);
}

// Stored format: {"verdicts":{code:"correct"|"incorrect"},"note":"..."} for flagged jobs, {"missed":[code],"note":"..."} for clean ones.
// Anything that isn't that JSON (older or hand-entered data) is kept as the note so it isn't lost.
export const parseAutoRejectFeedback = (raw) => {
    const feedback = { verdicts: {}, missed: [], note: '' };
    if (!raw) return feedback;
    let parsed = null;
    try { parsed = JSON.parse(raw); } catch (e) { parsed = null; }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        feedback.note = String(raw);
        return feedback;
    }
    if (parsed.verdicts && typeof parsed.verdicts === 'object') {
        Object.keys(parsed.verdicts).forEach(code => {
            if (parsed.verdicts[code] === 'correct' || parsed.verdicts[code] === 'incorrect') feedback.verdicts[code] = parsed.verdicts[code];
        });
    }
    if (Array.isArray(parsed.missed)) feedback.missed = parsed.missed.filter(code => typeof code === 'string');
    if (typeof parsed.note === 'string') feedback.note = parsed.note;
    return feedback;
}

const buildFeedbackBase = ({ verdicts, missed }, flagged, reasonCodes) => {
    const base = {};
    if (flagged) {
        const knownVerdicts = {};
        reasonCodes.forEach(code => { if (verdicts[code]) knownVerdicts[code] = verdicts[code]; });
        if (Object.keys(knownVerdicts).length) base.verdicts = knownVerdicts;
    } else if (missed.length) {
        base.missed = missed;
    }
    return base;
}

// The server rejects anything over 1000 characters, so cut the note (never the verdicts) until the serialised string fits
const fitNote = (base, note) => {
    if (JSON.stringify({ ...base, note }).length <= MAX_FEEDBACK_LENGTH) return note;
    let low = 0, high = note.length;
    while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        if (JSON.stringify({ ...base, note: note.slice(0, mid) }).length <= MAX_FEEDBACK_LENGTH) low = mid;
        else high = mid - 1;
    }
    return note.slice(0, low);
}

const serializeFeedback = (feedback, flagged, reasonCodes) => {
    const base = buildFeedbackBase(feedback, flagged, reasonCodes);
    const note = feedback.note.trim() ? fitNote(base, feedback.note) : '';
    const result = note ? { ...base, note } : base;
    // An empty string clears the stored feedback on the server
    return Object.keys(result).length ? JSON.stringify(result) : '';
}

const AutoRejectFeedback = ({ item, adminId, onSaved }) => {
    const flagged = item.autoReject == 1;
    const reviewedClean = !flagged && item.autoRejectChecked == 1;
    const reasons = getAutoRejectReasons(item);
    const reasonCodes = reasons.map(each => each.code);

    const [feedback, setFeedback] = useState(() => parseAutoRejectFeedback(item.autoRejectFeedback));
    const [showNote, setShowNote] = useState(() => !!parseAutoRejectFeedback(item.autoRejectFeedback).note);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const lastSent = useRef(item.autoRejectFeedback || '');
    const lastSaved = useRef(item.autoRejectFeedback || '');
    const requestCount = useRef(0);

    // Flagged with no reasons means an older backend - the bare "Auto Reject Job" tag is enough
    if (!(flagged && reasons.length) && !reviewedClean) return null;

    const save = (nextFeedback) => {
        const serialized = serializeFeedback(nextFeedback, flagged, reasonCodes);
        if (serialized === lastSent.current) return;
        lastSent.current = serialized;
        const requestId = ++requestCount.current;
        setSaving(true);
        setError('');
        saveAutoRejectFeedback(adminId, item.id, serialized)
            .then(res => {
                if (requestId !== requestCount.current) return;
                if (!res || res.status != 'success') throw new Error((res && res.message) || 'Feedback could not be saved');
                const data = res.data || {};
                const stored = data.feedback || '';
                lastSent.current = stored;
                lastSaved.current = stored;
                setSaving(false);
                setFeedback(parseAutoRejectFeedback(stored));
                onSaved && onSaved(item.id, {
                    autoRejectFeedback: data.feedback || null,
                    autoRejectFeedbackAt: data.feedback ? new Date().toISOString() : null,
                    autoRejectFeedbackBy: data.feedback ? adminId : null,
                    ...(Array.isArray(data.reasons) ? { autoRejectReasons: data.reasons } : {}),
                    ...(Array.isArray(data.reasonLabels) ? { autoRejectReasonLabels: data.reasonLabels } : {}),
                });
            })
            .catch(err => {
                if (requestId !== requestCount.current) return;
                lastSent.current = lastSaved.current;
                setSaving(false);
                const serverMessage = err && err.response && err.response.data && err.response.data.message;
                setError(serverMessage || err.message || 'Feedback could not be saved');
            });
    }

    const setVerdict = (code, verdict) => {
        const verdicts = { ...feedback.verdicts };
        if (verdicts[code] === verdict) delete verdicts[code];
        else verdicts[code] = verdict;
        const next = { ...feedback, verdicts };
        setFeedback(next);
        save(next);
    }

    const addMissed = (event) => {
        const code = event.target.value;
        if (!code || feedback.missed.includes(code)) return;
        const next = { ...feedback, missed: [...feedback.missed, code] };
        setFeedback(next);
        save(next);
    }

    const removeMissed = (code) => {
        const next = { ...feedback, missed: feedback.missed.filter(each => each !== code) };
        setFeedback(next);
        save(next);
    }

    const noteChange = (event) => {
        const base = buildFeedbackBase(feedback, flagged, reasonCodes);
        setFeedback({ ...feedback, note: fitNote(base, event.target.value) });
    }

    const clearFeedback = () => {
        const next = { verdicts: {}, missed: [], note: '' };
        setFeedback(next);
        setShowNote(false);
        save(next);
    }

    const remaining = MAX_FEEDBACK_LENGTH - serializeFeedback(feedback, flagged, reasonCodes).length;
    const missedLabel = (code) => {
        const criterion = missableCriteria.find(each => each.code === code);
        return criterion ? criterion.label : code;
    }

    return (
        <div className='auto-reject-feedback'>
            {
                flagged ?
                <div>
                    <div className='arf-heading'>Flagged for review - was each flag right?</div>
                    {reasons.map(each => (
                        <div className='arf-reason' key={each.code}>
                            <span className='arf-chip'>{each.label}</span>
                            <button type='button' className={`arf-verdict ${feedback.verdicts[each.code] === 'correct' ? 'arf-verdict-correct' : ''}`} onClick={() => setVerdict(each.code, 'correct')} title='This flag was right'>Right</button>
                            <button type='button' className={`arf-verdict ${feedback.verdicts[each.code] === 'incorrect' ? 'arf-verdict-incorrect' : ''}`} onClick={() => setVerdict(each.code, 'incorrect')} title='This flag was wrong'>Wrong</button>
                        </div>
                    ))}
                </div> :
                <div>
                    <span className='arf-clean'>AI reviewed, no issues</span>
                    {feedback.missed.map(code => (
                        <span className='arf-chip arf-chip-missed' key={code}>
                            Missed: {missedLabel(code)}
                            <span className='arf-remove' onClick={() => removeMissed(code)} title='Remove'>×</span>
                        </span>
                    ))}
                    <select className='arf-missed-select' value='' onChange={addMissed}>
                        <option value=''>Report a missed criterion</option>
                        {missableCriteria.filter(each => !feedback.missed.includes(each.code)).map(each => (
                            <option value={each.code} key={each.code}>{each.label}</option>
                        ))}
                    </select>
                </div>
            }
            {
                showNote ?
                <div className='arf-note'>
                    <textarea rows='3' value={feedback.note} placeholder='Note for the whole job (optional)' onChange={noteChange} onBlur={() => save(feedback)}></textarea>
                    <div className='arf-note-footer'>
                        {remaining < 200 ? <span className={remaining < 50 ? 'arf-counter arf-counter-low' : 'arf-counter'}>{remaining} characters left</span> : null}
                        <button type='button' className='arf-link' onClick={() => save(feedback)}>Save note</button>
                    </div>
                </div> :
                <span className='arf-link' onClick={() => setShowNote(true)}>Add note</span>
            }
            <div className='arf-status'>
                {saving ? <span>Saving...</span> : null}
                {!saving && error ? <span className='arf-error'>{error}</span> : null}
                {!saving && !error && item.autoRejectFeedback && item.autoRejectFeedbackAt ?
                    <span>Feedback saved on {moment(item.autoRejectFeedbackAt).format('DD-MM-YYYY hh:mm A')}</span> : null}
                {!saving && item.autoRejectFeedback ? <span className='arf-link' onClick={clearFeedback}>Clear feedback</span> : null}
            </div>
        </div>
    )
}

AutoRejectFeedback.propTypes = {
    item: PropTypes.object.isRequired,
    adminId: PropTypes.any,
    onSaved: PropTypes.func,
}

export default AutoRejectFeedback;
